import { ART_PAL, fade, mix, n, shade, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Color sort scene (placeholder until the raster of docs/art-codex-task-puzzles.md): a calm ceramics workshop at
 * dusk (a bright studio in light) — a soft window light over the middle where the tubes stand, and at both edges a
 * shelf with glazed bowls and a few loose counters. Colours of the glazes are muted so the counters of the board stay
 * the brightest things on screen. Motifs are marked `data-motif="<x>"`. No filters, no text.
 */
export function colorSortScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `scs${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const top = P.dark ? mix(P.bg, P.purple, 0.12) : mix(P.purple, '#FFFFFF', 0.88);
  const mid = P.dark ? mix(P.bg, P.orange, 0.06) : mix(P.orange, '#FFFFFF', 0.9);
  const low = P.dark ? mix(P.bg, P.sky, 0.05) : mix(P.sky, '#FFFFFF', 0.9);
  const wood = P.dark ? mix(P.bg, P.orange, 0.3) : mix(P.orange, '#FFFFFF', 0.55);
  const mute = (c: string) => (P.dark ? mix(P.bg, c, 0.45) : mix(c, '#FFFFFF', 0.35));
  const glazes = [P.orange, P.teal, P.purple, P.gold].map(mute);

  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, low)}` +
    fade(`${p}win`, P.dark ? P.sky : '#FFFFFF', P.dark ? 0.1 : 0.5) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.26 : 0.35) +
    `</defs>`;

  /** a shelf with two bowls and a stack of counters */
  const shelf = (x: number, y: number, s: number, k: number) => {
    const bowl = (bx: number, c: string) =>
      `<path d="M${n(bx - 34 * s)} ${n(y - 2)}a${n(34 * s)} ${n(26 * s)} 0 0 0 ${n(68 * s)} 0z" fill="${c}"/>` +
      `<ellipse cx="${n(bx)}" cy="${n(y - 2)}" rx="${n(34 * s)}" ry="${n(6 * s)}" fill="${shade(c, 0.2)}"/>`;
    const counter = (cx: number, cy: number, c: string) =>
      `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(11 * s)}" fill="${c}"/>`;
    return (
      `<g data-motif="${Math.round(x)}" opacity="${P.dark ? 0.6 : 0.75}">` +
      `<rect x="${n(x - 110 * s)}" y="${n(y)}" width="${n(220 * s)}" height="${n(10 * s)}" rx="3" fill="${wood}"/>` +
      bowl(x - 55 * s, glazes[k % 4]!) +
      bowl(x + 40 * s, glazes[(k + 1) % 4]!) +
      counter(x + 90 * s, y - 12 * s, glazes[(k + 2) % 4]!) +
      counter(x + 92 * s, y - 34 * s, glazes[(k + 3) % 4]!) +
      `</g>`
    );
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.32)}" rx="${n(W * 0.4)}" ry="${n(H * 0.38)}" fill="url(#${p}win)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.5)}" rx="${n(W * 0.3)}" ry="${n(H * 0.36)}" fill="url(#${p}calm)"/>`;
  if (wide)
    art +=
      shelf(220, 300, 1.2, 0) + shelf(250, 620, 1.1, 2) + shelf(1380, 340, 1.2, 1) + shelf(1350, 660, 1.1, 3);
  else art += shelf(10, 790, 0.5, 0) + shelf(W - 10, 795, 0.5, 1);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">${defs}${art}</svg>`;
}

/** Header accent of Color sort: four glazed dots in a row (repeat-x tile, 40 × 8). */
export function colorSortAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const dot = (x: number, c: string) => `<circle cx="${x}" cy="4" r="2.6" fill="${c}"/>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 8" width="40" height="8">${dot(5, P.orange)}${dot(15, P.teal)}${dot(25, P.purple)}${dot(35, P.gold)}</svg>`;
}

export default defineScene({ game: 'color-sort', scene: colorSortScene, accent: colorSortAccent });
